/**
 * Boat Configuration & Power Calculations
 * 
 * Provides boat mass calculations for power estimation
 * Based on standard hull weights and crew weight categories
 */

// Standard hull weights (kg) - based on FISA minimum weights
export const HULL_WEIGHTS = {
  '1x': 14,      // Single scull
  '2-': 27,      // Coxless pair
  '2x': 27,      // Double scull
  '4-': 50,      // Coxless four
  '4+': 51,      // Coxed four
  '4x': 52,      // Quad scull
  '4x+': 53,     // Coxed quad
  '8+': 96,      // Eight
  '8x+': 96,     // Octuple (rare)
  'c2_erg': 0    // Ergometer - no boat mass needed
};

// Crew count per boat class
export const CREW_COUNTS = {
  '1x': 1,
  '2-': 2,
  '2x': 2,
  '4-': 4,
  '4+': 4,
  '4x': 4,
  '4x+': 4,
  '8+': 8,
  '8x+': 8,
  'c2_erg': 1
};

// Boats with coxswain
export const HAS_COXSWAIN = {
  '1x': false,
  '2-': false,
  '2x': false,
  '4-': false,
  '4+': true,
  '4x': false,
  '4x+': true,
  '8+': true,
  '8x+': true,
  'c2_erg': false
};

// Average rower weights by category (kg)
export const WEIGHT_CATEGORIES = {
  'men': {
    label: 'Men (Open)',
    avgWeight: 87,      // Average competitive male rower
    description: 'Open weight men'
  },
  'women': {
    label: 'Women (Open)', 
    avgWeight: 72,      // Average competitive female rower
    description: 'Open weight women'
  },
  'lwt_men': {
    label: 'Lightweight Men',
    avgWeight: 70,      // Max 72.5kg, crew avg 70kg
    description: 'Lightweight men (≤72.5kg)'
  },
  'lwt_women': {
    label: 'Lightweight Women',
    avgWeight: 57,      // Max 59kg, crew avg 57kg
    description: 'Lightweight women (≤59kg)'
  }
};

// Coxswain weights (minimum requirements)
export const COXSWAIN_WEIGHTS = {
  'men': 55,        // Min 55kg for men's boats
  'women': 50,      // Min 50kg for women's boats (some federations)
  'lwt_men': 55,    // Same minimum
  'lwt_women': 50   // Same minimum
};

/**
 * Calculate total boat mass (hull + crew + cox)
 * 
 * @param {string} boatClass - Boat class ID (e.g., '4+', '8+')
 * @param {string} weightCategory - Weight category ('men', 'women', 'lwt_men', 'lwt_women')
 * @param {Object} options - Additional options
 * @param {boolean} options.isCoxswain - Whether the user is a coxswain
 * @param {number} options.crewAvgWeight - Average weight of crew (used for coxswains)
 * @returns {number} Total mass in kg
 */
export function calculateBoatMass(boatClass, weightCategory = 'men', options = {}) {
  const { isCoxswain = false, crewAvgWeight = null } = options;
  
  const hullWeight = HULL_WEIGHTS[boatClass] || 0;
  const crewCount = CREW_COUNTS[boatClass] || 1;
  const hasCox = HAS_COXSWAIN[boatClass] || false;
  
  // Determine crew weight per person
  let avgWeightPerCrew;
  if (isCoxswain && crewAvgWeight && crewAvgWeight > 0) {
    // Coxswain: Use their specified crew average weight
    avgWeightPerCrew = crewAvgWeight;
  } else {
    // Rower: Use weight category average
    const category = WEIGHT_CATEGORIES[weightCategory] || WEIGHT_CATEGORIES['men'];
    avgWeightPerCrew = category.avgWeight;
  }
  
  const crewWeight = crewCount * avgWeightPerCrew;
  const coxWeight = hasCox ? (COXSWAIN_WEIGHTS[weightCategory] || 55) : 0;
  
  const totalMass = hullWeight + crewWeight + coxWeight;
  
  return totalMass;
}

/**
 * Calculate power from GPS speed using empirical formula
 * Based on fluid dynamics: Power ∝ velocity³
 * 
 * Formula: P = k × v³ × m^(1/3)
 * Where k ≈ 2.8 is an empirical constant for rowing shells
 * 
 * @param {number} speed - Speed in m/s
 * @param {number} totalMass - Total boat mass in kg
 * @returns {number} Power in watts (per crew member)
 */
export function calculatePowerFromSpeed(speed, totalMass) {
  if (speed <= 0 || totalMass <= 0) return 0;
  
  // Empirical formula for rowing power
  // P = 2.8 × v³ × m^(1/3)
  // This accounts for drag coefficient, water density, and hull characteristics
  const k = 2.8;
  const power = k * Math.pow(speed, 3) * Math.pow(totalMass, 1/3);
  
  return Math.round(power);
}

/**
 * Calculate power per rower (for crew boats)
 */
export function calculatePowerPerRower(speed, boatClass, weightCategory = 'men') {
  const totalMass = calculateBoatMass(boatClass, weightCategory);
  const totalPower = calculatePowerFromSpeed(speed, totalMass);
  const crewCount = CREW_COUNTS[boatClass] || 1;
  
  return Math.round(totalPower / crewCount);
}

/**
 * Calculate calories burned from power and time
 *
 * Formula: calories = (power × time) / (4184 × η)
 *   where η is the metabolic-to-mechanical efficiency.
 *
 * Rowing efficiency is 18-23% (lower than cycling because of the recovery
 * phase, which is largely unloaded but still consumes energy). Concept2's
 * own published calorie-from-power relationship implies ~20%, so we use
 * 0.20 here. This raises the formula's calorie estimate by ~25% over the
 * old 0.25 value and aligns with the PM5 hardware to within ±5% at
 * steady-state pace.
 *
 * @param {number} powerWatts - Power in watts
 * @param {number} seconds - Duration in seconds
 * @returns {number} Calories (kcal)
 */
export function calculateCaloriesFromPower(powerWatts, seconds) {
  if (powerWatts <= 0 || seconds <= 0) return 0;

  // Energy in Joules = Power × Time
  const energyJoules = powerWatts * seconds;

  // Convert to kcal (1 kcal = 4184 J) and apply rowing-specific 20% efficiency.
  const efficiency = 0.20;
  const calories = energyJoules / (4184 * efficiency);

  return Math.round(calories);
}

/**
 * Calculate check factor from speed data.
 * Check = peak deceleration during the recovery phase.
 * Lower is better (less energy lost to stopping the boat).
 *
 * The recovery time is derived from a stroke-rate-dependent drive fraction.
 * Drive fraction grows from ~0.35 at low rates (e.g. UT2 at 18 spm) to
 * ~0.45 at sprint rates (e.g. 40 spm), so:
 *   recovery_time = stroke_duration × (1 − driveFraction)
 *
 * Using a flat 0.5 ratio (the previous implementation) under-counts recovery
 * time at low rates and over-counts at sprint rates, which biases the check
 * factor in opposite directions. The drive-fraction model matches biomech
 * literature and aligns with what NK EmPower / XBoat oarlocks publish.
 *
 * @param {number} speedAtCatch    - Speed at catch (start of drive) m/s
 * @param {number} speedAtFinish   - Speed at finish (end of drive)   m/s
 * @param {number} strokeDuration  - Duration of one full stroke    s
 * @param {number} [strokeRate]    - Optional stroke rate (spm) for adaptive drive fraction. Default 24.
 * @returns {number} Check factor in m/s² (typical range 0.5 - 5.0)
 */
export function calculateCheckFactor(speedAtCatch, speedAtFinish, strokeDuration, strokeRate = 24) {
  if (strokeDuration <= 0) return 0;

  // Drive fraction grows linearly with rate over [18..40] spm.
  // Anchor points: 0.35 at 18 spm, 0.45 at 40 spm.
  const rawFraction = 0.35 + ((strokeRate - 18) / (40 - 18)) * 0.10;
  const driveFraction = Math.max(0.33, Math.min(0.45, rawFraction));

  const recoveryTime = strokeDuration * (1 - driveFraction);
  if (recoveryTime <= 0) return 0;

  const deceleration = (speedAtFinish - speedAtCatch) / recoveryTime;
  return Math.abs(deceleration);
}

/**
 * Calculate average split from total distance and time
 * 
 * @param {number} distanceMeters - Total distance in meters
 * @param {number} elapsedSeconds - Total elapsed time in seconds
 * @returns {number} Average split in seconds per 500m
 */
export function calculateAverageSplit(distanceMeters, elapsedSeconds) {
  if (distanceMeters <= 0 || elapsedSeconds <= 0) return 0;
  
  // Average pace = total time / (distance / 500)
  return elapsedSeconds / (distanceMeters / 500);
}

/**
 * Calculate distance per stroke
 * 
 * @param {number} distanceMeters - Total distance in meters
 * @param {number} strokeCount - Number of strokes
 * @returns {number} Distance per stroke in meters
 */
export function calculateDistancePerStroke(distanceMeters, strokeCount) {
  if (strokeCount <= 0) return 0;
  return distanceMeters / strokeCount;
}

/**
 * Calculate work per stroke
 * 
 * @param {number} powerWatts - Current power in watts
 * @param {number} strokeRate - Strokes per minute
 * @returns {number} Work per stroke in Joules
 */
export function calculateWorkPerStroke(powerWatts, strokeRate) {
  if (strokeRate <= 0 || powerWatts <= 0) return 0;
  
  // Stroke duration in seconds
  const strokeDuration = 60 / strokeRate;
  
  // Work = Power × Time
  return Math.round(powerWatts * strokeDuration);
}

/**
 * Estimate stroke length from distance per stroke
 * Rough estimate: stroke length ≈ distance_per_stroke / 2
 * (Each stroke propels the boat roughly 2× the catch-to-finish distance)
 * 
 * @param {number} distancePerStroke - Distance traveled per stroke in meters
 * @returns {number} Estimated stroke length in meters
 */
export function estimateStrokeLength(distancePerStroke) {
  if (distancePerStroke <= 0) return 0;
  
  // Empirical factor: boat travels ~2× the actual stroke arc length
  // Plus some slip (blade not 100% efficient)
  const factor = 2.2;
  return distancePerStroke / factor;
}

export default {
  HULL_WEIGHTS,
  CREW_COUNTS,
  HAS_COXSWAIN,
  WEIGHT_CATEGORIES,
  COXSWAIN_WEIGHTS,
  calculateBoatMass,
  calculatePowerFromSpeed,
  calculatePowerPerRower,
  calculateCaloriesFromPower,
  calculateCheckFactor,
  calculateAverageSplit,
  calculateDistancePerStroke,
  calculateWorkPerStroke,
  estimateStrokeLength
};
